import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../api';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#B85C38',
  danger: '#B54135',
};

const extractPayouts = response => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const money = value => `R${Number(value || 0).toFixed(2)}`;
const dateOnly = value => (value ? new Date(value).toLocaleDateString() : '-');
const PAGE_SIZE = 10;

export default function RestaurantPayouts() {
  const [status, setStatus] = useState('pending');
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [confirmPayout, setConfirmPayout] = useState(null);
  const [page, setPage] = useState(1);

  const loadPayouts = async (nextStatus = status) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getRestaurantPayouts(nextStatus);
      setPayouts(extractPayouts(response));
    } catch (err) {
      setError(err.message || 'Could not load restaurant payouts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayouts(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    setPage(1);
  }, [status, payouts.length]);

  const totals = useMemo(() => {
    return payouts.reduce(
      (acc, payout) => {
        acc.amount += Number(payout.amount || 0);
        acc.grossSales += Number(payout.grossSales || 0);
        acc.platformFees += Number(payout.platformFees || 0);
        acc.orders += Number(payout.ordersCount || 0);
        return acc;
      },
      { amount: 0, grossSales: 0, platformFees: 0, orders: 0 },
    );
  }, [payouts]);

  const handleMarkPaid = async payout => {
    try {
      setActionId(payout.id);
      await api.markRestaurantPayoutPaid(payout.id);
      setConfirmPayout(null);
      await loadPayouts(status);
    } catch (err) {
      setError(err.message || 'Could not mark restaurant payout paid.');
    } finally {
      setActionId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(payouts.length / PAGE_SIZE));
  const paginatedPayouts = payouts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Layout>
      <PageHeader
        eyebrow="Restaurant Finance"
        title="Weekly Restaurant Payouts"
        subtitle="Pay restaurants on Monday for the completed Saturday-Friday payout week."
      />

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Payout</span>
          <strong style={styles.summaryValue}>{money(totals.amount)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Gross Sales</span>
          <strong style={styles.summaryValue}>{money(totals.grossSales)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Platform Fees</span>
          <strong style={styles.summaryValue}>{money(totals.platformFees)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Orders</span>
          <strong style={styles.summaryValue}>{totals.orders}</strong>
        </div>
      </div>

      <div style={styles.toolbar}>
        <select value={status} onChange={event => setStatus(event.target.value)} style={styles.select}>
          <option value="pending">Pending payouts</option>
          <option value="paid">Paid payouts</option>
          <option value="">All payouts</option>
        </select>
        <button style={styles.refreshButton} onClick={() => loadPayouts(status)}>Refresh</button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.tableShell}>
        {loading ? (
          <div style={styles.emptyState}>Loading restaurant payouts...</div>
        ) : payouts.length === 0 ? (
          <div style={styles.emptyState}>No restaurant payouts found.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Restaurant</th>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>Payable</th>
                <th style={styles.th}>Orders</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayouts.map(payout => (
                <tr key={payout.id}>
                  <td style={styles.td}>
                    <strong>{payout.restaurant?.name || 'Restaurant'}</strong>
                    <div style={styles.muted}>{payout.restaurant?.phone || payout.restaurant?.ownerEmail || '-'}</div>
                  </td>
                  <td style={styles.td}>{dateOnly(payout.periodStart)} - {dateOnly(payout.periodEnd)}</td>
                  <td style={styles.td}>{dateOnly(payout.payableOn)}</td>
                  <td style={styles.td}>{payout.ordersCount || 0}</td>
                  <td style={styles.td}>
                    <strong>{money(payout.amount)}</strong>
                    <div style={styles.muted}>Sales {money(payout.grossSales)} | Fees {money(payout.platformFees)}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(payout.status === 'paid' ? styles.badgePaid : styles.badgePending) }}>
                      {payout.status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                    {payout.paidAt && <div style={styles.muted}>{new Date(payout.paidAt).toLocaleString()}</div>}
                  </td>
                  <td style={styles.td}>
                    {payout.status === 'paid' ? (
                      <span style={styles.done}>Done</span>
                    ) : (
                      <button style={styles.payButton} onClick={() => setConfirmPayout(payout)} disabled={actionId === payout.id}>
                        {actionId === payout.id ? 'Saving...' : 'Mark Paid'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && payouts.length > 0 && (
          <div style={styles.paginationBar}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page === 1}
              onClick={() => setPage(current => Math.max(1, current - 1))}>
              Previous
            </button>
            <span style={styles.pageText}>Page {page} of {totalPages}</span>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page === totalPages}
              onClick={() => setPage(current => Math.min(totalPages, current + 1))}>
              Next
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmPayout}
        title="Mark restaurant payout paid"
        message={
          confirmPayout
            ? `Mark ${money(confirmPayout.amount)} as paid for ${confirmPayout.restaurant?.name || 'this restaurant'}? Only do this after the money has been paid.`
            : ''
        }
        confirmLabel="Mark paid"
        tone="success"
        loading={!!actionId}
        onCancel={() => setConfirmPayout(null)}
        onConfirm={() => handleMarkPaid(confirmPayout)}
      />
    </Layout>
  );
}

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    marginBottom: 18,
  },
    summaryCard: {
      backgroundColor: COLORS.offWhite,
      border: `1px solid ${COLORS.softBorder}`,
      borderRadius: 8,
      padding: 16,
      minHeight: 88,
    },
    summaryLabel: {
      display: 'block',
      color: COLORS.mutedText,
      fontSize: 12,
      fontWeight: 800,
      textTransform: 'uppercase',
      marginBottom: 8,
    },
    summaryValue: {
      display: 'block',
      color: COLORS.forestGreen,
      fontSize: 24,
      lineHeight: 1.2,
    },
  toolbar: {
    display: 'flex',
    gap: 10,
    marginBottom: 14,
  },
  select: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    borderRadius: 6,
    padding: '10px 12px',
    fontWeight: 800,
  },
  refreshButton: {
    border: 'none',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    borderRadius: 6,
    padding: '10px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: `1px solid ${COLORS.danger}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontWeight: 800,
  },
  tableShell: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '15px 16px',
    color: COLORS.forestGreen,
    borderTop: `1px solid ${COLORS.softBorder}`,
    verticalAlign: 'top',
  },
  muted: { color: COLORS.mutedText, fontSize: 12, marginTop: 4 },
  badge: { borderRadius: 999, padding: '6px 10px', fontWeight: 900, fontSize: 12 },
  badgePaid: { backgroundColor: '#E7F6ED', color: COLORS.success },
  badgePending: { backgroundColor: '#FFF1E8', color: COLORS.warning },
  payButton: {
    backgroundColor: COLORS.heritageGold,
    color: COLORS.forestGreen,
    border: 'none',
    borderRadius: 6,
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 900,
  },
  done: { color: COLORS.success, fontWeight: 900 },
  emptyState: { padding: 32, textAlign: 'center', color: COLORS.mutedText },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 12,
    borderTop: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#FBF6EA',
  },
  pageButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.mutedText,
    fontWeight: 800,
    fontSize: 13,
  },
};
